import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { PortfolioDataWrapper } from '~/components/portfolio/portfolio-data-wrapper.tsx';
import { useHydrateInstrumentDisplayPayload } from '~/components/root/use-hydrate-instrument-display-payload.ts';
import { useWarmInstrumentDisplayData } from '~/components/root/use-warm-instrument-display-data.ts';
import { useWarmQuery } from '~/components/root/use-warm-query.ts';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/shadcn/card.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import type { PortfolioWithAllRelationsType } from '~/database/supabase/portfolio/types.ts';
import { useTranslations } from '~/i18n/use-translations.ts';
import type { PortfolioPageData } from '~/server/portfolio/get-portfolio-page-data.ts';
import {
	getPortfolioOverviewQueryOptions,
	getPortfolioTransactionsQueryOptions,
} from '~/server/portfolio/query-options.ts';
import { useStore } from '~/store/index.ts';
import { PORTFOLIO_GRAPH_CARD_KEYS, PORTFOLIO_TABLE_TOGGLEABLE_COLUMN_KEYS } from '~/store/types.ts';
import { getPortfolioInstrumentDisplayTickers } from '~/utils/instrument-display-tickers.ts';

export const Route = createFileRoute('/portfolios/$portfolioId')({
	component: PortfolioRouteComponent,
	loader: async ({ context, params }): Promise<PortfolioPageData> => {
		const portfolioId = Number(params.portfolioId);

		return context.queryClient.ensureQueryData(getPortfolioOverviewQueryOptions(portfolioId));
	},
	pendingComponent: PortfolioRoutePending,
	pendingMinMs: 250,
});

function PortfolioRoutePending() {
	return (
		<ScrollArea
			classNames={{
				root: 'flex h-portfolio-body-height w-full flex-col pr-1 pl-1 sm:pr-3',
				viewport: 'h-portfolio-body-height w-full [&>div]:block!',
			}}
		>
			<div className="flex w-full flex-col gap-y-1 py-1">
				<Skeleton className="h-[500px] w-full shrink-0 rounded-md border border-border bg-card" />
				<div className="w-full p-2">
					<Skeleton className="h-[500px] w-full rounded-md border border-border bg-card" />
				</div>
				<div className="grid w-full grid-cols-1 gap-2 p-2 lg:grid-cols-2">
					<Skeleton className="h-44 w-full rounded-md border border-border bg-card" />
					<Skeleton className="h-44 w-full rounded-md border border-border bg-card" />
				</div>
			</div>
		</ScrollArea>
	);
}

function isFullPortfolio(portfolio: PortfolioPageData['activePortfolio']): portfolio is PortfolioWithAllRelationsType {
	return Boolean(portfolio && 'portfolio_settings' in portfolio);
}

function PortfolioRouteComponent() {
	const t = useTranslations();
	const { portfolioId: portfolioIdParam } = Route.useParams();
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const portfolioId = Number(portfolioIdParam);
	const isTransactionsRoute = pathname.includes('/transactions');
	const isSettingsRoute = pathname.endsWith('/settings');
	const { data: loaderData } = useSuspenseQuery(getPortfolioOverviewQueryOptions(portfolioId));
	const portfolio = loaderData.activePortfolio;
	const fullPortfolio = isFullPortfolio(portfolio) ? portfolio : null;
	const setPortfolioGraphCardVisibility = useStore((state) => state.setPortfolioGraphCardVisibility);
	const setPortfolioTableColumnVisibility = useStore((state) => state.setPortfolioTableColumnVisibility);
	const preferenceHydrationSignatureRef = useRef<string>('');

	useHydrateInstrumentDisplayPayload(loaderData.initialInstrumentDisplayData);

	useWarmInstrumentDisplayData(getPortfolioInstrumentDisplayTickers(fullPortfolio));
	useWarmQuery({
		...getPortfolioTransactionsQueryOptions(portfolioId),
		enabled: !isTransactionsRoute && !isSettingsRoute && portfolioId > 0,
	});

	useEffect(() => {
		if (isTransactionsRoute || !fullPortfolio) {
			return;
		}

		const signature = JSON.stringify({
			graphCardVisibility: loaderData.preferenceOverrides.graphCardVisibility,
			portfolioId: fullPortfolio.id,
			tableColumnVisibility: loaderData.preferenceOverrides.tableColumnVisibility,
		});

		if (preferenceHydrationSignatureRef.current === signature) {
			return;
		}

		preferenceHydrationSignatureRef.current = signature;

		for (const cardKey of PORTFOLIO_GRAPH_CARD_KEYS) {
			const isVisible = loaderData.preferenceOverrides.graphCardVisibility[cardKey];
			if (typeof isVisible === 'boolean') {
				setPortfolioGraphCardVisibility(fullPortfolio.id, cardKey, isVisible);
			}
		}

		for (const columnKey of PORTFOLIO_TABLE_TOGGLEABLE_COLUMN_KEYS) {
			const isVisible = loaderData.preferenceOverrides.tableColumnVisibility[columnKey];
			if (typeof isVisible === 'boolean') {
				setPortfolioTableColumnVisibility(fullPortfolio.id, columnKey, isVisible);
			}
		}
	}, [
		fullPortfolio,
		isTransactionsRoute,
		loaderData.preferenceOverrides.graphCardVisibility,
		loaderData.preferenceOverrides.tableColumnVisibility,
		setPortfolioGraphCardVisibility,
		setPortfolioTableColumnVisibility,
	]);

	if (!portfolio) {
		return (
			<div className="h-portfolio-body-height overflow-auto px-4 py-4 sm:px-6">
				<div className="mx-auto flex w-full max-w-[1280px] flex-col gap-4">
					<Card className="border-border/70 shadow-sm">
						<CardHeader>
							<CardTitle>{t('portfolio.notFound')}</CardTitle>
						</CardHeader>
						<CardContent className="text-muted-foreground text-sm">{t('portfolio.notFoundDescription')}</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	return (
		<ScrollArea
			classNames={{
				root: 'flex h-portfolio-body-height w-full flex-col pr-1 pl-1 sm:pr-3',
				viewport: 'h-portfolio-body-height w-full [&>div]:block!',
			}}
		>
			{isTransactionsRoute ? (
				<Outlet />
			) : fullPortfolio ? (
				<PortfolioDataWrapper
					activePortfolioId={fullPortfolio.id}
					historyDebugBreakdownVisible={loaderData.preferenceOverrides.historyDebugBreakdownVisible}
					initialInstrumentFetchEntries={loaderData.initialInstrumentDisplayData?.instrumentFetchEntries}
					outletWrapper={<Outlet />}
					pathname={pathname}
					portfolio={fullPortfolio}
					portfolioInfo={loaderData.portfolioInfo}
					portfolioValues={loaderData.portfolioValues}
					userLocale={loaderData.userLocale}
				/>
			) : null}
		</ScrollArea>
	);
}
